/**
 * @file Defines the Events that can be dispatched within Class Search.
 */
import type { ArtistDataItem } from '@engage/api-v2/generated/../../api-v2-models/generated/ArtistDataItem';
import type { ClassReducerState } from '@engage/classes';
import type { ClassPreview } from '@engage/library';
import type { MetadataSelectorState } from '@engage/metadata';
import type { QueryParams } from './types';

/**
 * Received metadata updates from redux.
 * @param metadataState
 */
export const metadata = (metadataState: MetadataSelectorState['entities']) =>
  ({ type: 'metadata', metadata: metadataState } as const);

/**
 * Trigger query params to be parsed from URL.
 */
export const parseQueryParams = (queryParams: QueryParams) =>
  ({ type: 'parseQueryParams', queryParams } as const);

/**
 * Request a search to happen
 * @param query the search term
 * @param popularQuery whether the search term came from a popular search or not
 */
export const input = (query: string, popularQuery = false) =>
  ({ type: 'input', query, popularQuery } as const);

/**
 * Request to view an artist.
 * @param artistId the artist id
 * @param query the search query. Tthis would be the same if initialized within the UI. From `parseQueryParams`, this
 * would be the query to return to when the user clicks back.
 * @param listPosition the position of the artist in the list of artists when clicked
 */
export const loadArtist = (artistId: string, query: string, listPosition: number) =>
  ({ type: 'loadArtist', artistId, query, listPosition } as const);

/**
 * Artist finished loading and is ready to view.
 */
export const viewingArtist = () => ({ type: 'viewingArtist' } as const);

/**
 * Searching (XHR requests) has started.
 */
export const searching = () => ({ type: 'searching' } as const);

/**
 * Merge in classes from redux. This is used to sync bookmark state.
 * @param reduxClasses redux classes
 */
export const syncRedux = (reduxClasses: ClassReducerState) =>
  ({ type: 'syncRedux', reduxClasses } as const);

/**
 * Show no results title. XHR requests returned no results.
 */
export const noResults = () => ({ type: 'noResults' } as const);

/**
 * XHR requests have completed.
 * @param artists
 * @param classes
 */
export const showResults = (artists: ArtistDataItem[], classes: ClassPreview[]) =>
  ({ type: 'showResults', artists, classes } as const);

/**
 * UI request to go back. Back could mean a previous search or back out of Class Search.
 */
export const back = () => ({ type: 'back' } as const);

/**
 * Set previous page state
 */
export const setPreviousPage = (previousPage: string) =>
  ({ type: 'setPreviousPage', previousPage } as const);

/**
 * Change bookmark state of class preview indicated by `rideId`.
 * @param rideId
 * @param isBookmarked
 */
export const bookmark = (rideId: string, isBookmarked: boolean) =>
  ({ type: 'bookmark', rideId, isBookmarked } as const);

export const showedViewedSearchScreenAnalytics = () =>
  ({ type: 'showedViewedSearchScreenAnalytics' } as const);

export type Event = ReturnType<
  | typeof metadata
  | typeof input
  | typeof loadArtist
  | typeof viewingArtist
  | typeof searching
  | typeof showResults
  | typeof noResults
  | typeof parseQueryParams
  | typeof back
  | typeof bookmark
  | typeof syncRedux
  | typeof showedViewedSearchScreenAnalytics
  | typeof setPreviousPage
>;
